import React, { useState, useEffect, useCallback } from 'react';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { validatePassword, PasswordValidationResult, getPasswordStrengthColor, getPasswordStrengthBg } from '../utils/passwordValidation';

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  firstName?: string;
  lastName?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  showValidation?: boolean;
  onValidationChange?: (result: PasswordValidationResult) => void;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  firstName,
  lastName,
  label = 'Password',
  placeholder = 'Enter your password',
  required = false,
  className = '',
  showValidation = true,
  onValidationChange
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [validation, setValidation] = useState<PasswordValidationResult>({
    isValid: false,
    errors: [],
    strength: 'weak',
    checks: {
      minLength: false,
      hasUppercase: false,
      hasNumber: false,
      hasSpecialChar: false,
      noName: true,
      noContinuous: true,
    }
  });

  // Memoize the validation logic to prevent infinite re-renders
  const performValidation = useCallback(() => {
    const result = validatePassword(value, firstName, lastName);
    setValidation(result);
    onValidationChange?.(result);
  }, [value, firstName, lastName, onValidationChange]);

  useEffect(() => {
    performValidation();
  }, [value, firstName, lastName]); // Remove onValidationChange from dependencies

  const handleFocus = () => {
    setIsFocused(true);
    setHasBeenFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getCheckIcon = (isValid: boolean) => {
    return isValid ? (
      <CheckCircleIcon className="w-4 h-4 text-green-500" />
    ) : (
      <XCircleIcon className="w-4 h-4 text-red-500" />
    );
  };

  const shouldShowValidation = showValidation && (isFocused || hasBeenFocused) && value;

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          className={`input-admin pr-10 ${!validation.isValid && value ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'focus:border-admin-500 focus:ring-admin-500'} transition-colors duration-200`}
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
          {showPassword ? (
            <EyeSlashIcon className="w-5 h-5" />
          ) : (
            <EyeIcon className="w-5 h-5" />
          )}
        </button>
      </div>

      {shouldShowValidation && (
        <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out">
          {/* Password Strength Indicator */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400 font-medium">Password Strength:</span>
              <span className={`font-semibold capitalize ${getPasswordStrengthColor(validation.strength)}`}>
                {validation.strength}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ease-out ${
                  validation.strength === 'weak' 
                    ? 'bg-red-400' 
                    : validation.strength === 'medium' 
                    ? 'bg-yellow-400' 
                    : 'bg-green-400'
                }`}
                style={{
                  width: validation.strength === 'weak' ? '33%' : validation.strength === 'medium' ? '66%' : '100%'
                }}
              ></div>
            </div>
          </div>

          {/* Password Requirements Checklist */}
          <div className="space-y-1">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password Requirements:</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className={`flex items-center space-x-2 p-2 rounded-md transition-colors duration-200 ${
                validation.checks.minLength ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                {getCheckIcon(validation.checks.minLength)}
                <span className={`${validation.checks.minLength ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'} font-medium`}>
                  At least 8 characters
                </span>
              </div>
              
              <div className={`flex items-center space-x-2 p-2 rounded-md transition-colors duration-200 ${
                validation.checks.hasUppercase ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                {getCheckIcon(validation.checks.hasUppercase)}
                <span className={`${validation.checks.hasUppercase ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'} font-medium`}>
                  One uppercase letter (A-Z)
                </span>
              </div>
              
              <div className={`flex items-center space-x-2 p-2 rounded-md transition-colors duration-200 ${
                validation.checks.hasNumber ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                {getCheckIcon(validation.checks.hasNumber)}
                <span className={`${validation.checks.hasNumber ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'} font-medium`}>
                  One number (0-9)
                </span>
              </div>
              
              <div className={`flex items-center space-x-2 p-2 rounded-md transition-colors duration-200 ${
                validation.checks.hasSpecialChar ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                {getCheckIcon(validation.checks.hasSpecialChar)}
                <span className={`${validation.checks.hasSpecialChar ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'} font-medium`}>
                  One special character (!@#$%^&*)
                </span>
              </div>
              
              <div className={`flex items-center space-x-2 p-2 rounded-md transition-colors duration-200 ${
                validation.checks.noName ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                {getCheckIcon(validation.checks.noName)}
                <span className={`${validation.checks.noName ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'} font-medium`}>
                  No personal names
                </span>
              </div>
              
              <div className={`flex items-center space-x-2 p-2 rounded-md transition-colors duration-200 ${
                validation.checks.noContinuous ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'
              }`}>
                {getCheckIcon(validation.checks.noContinuous)}
                <span className={`${validation.checks.noContinuous ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'} font-medium`}>
                  No continuous patterns (123, abc)
                </span>
              </div>
            </div>
          </div>

          {/* Success Message */}
          {validation.isValid && (
            <div className="mt-3 p-2 bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md">
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  Great! Your password meets all security requirements.
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordInput;